
import { Component, OnInit, Input } from '@angular/core';
import { ModalController, ActionSheetController } from '@ionic/angular';
import { Metodosui } from '../../services/metodosui';
import { ImageEditorComponent } from '../image-editor/image-editor.component';

// Premium Style Updated

@Component({
    selector: 'app-measure-calculator',
    templateUrl: './measure-calculator.component.html',
    styleUrls: ['./measure-calculator.component.scss'],
    standalone: false
})
export class MeasureCalculatorComponent implements OnInit {

    @Input() initialDetails: any[] = [];
    @Input() initialUnit: string = 'm';

    details: any[] = [];
    totalArea: number = 0;
    unit: string = 'm';

    current: any = {
        descripcion: '',
        ancho: 0,
        alto: 0,
        unidad: 'm',
        foto: null,
        originalFoto: null,
        annotations: []
    };

    constructor(
        private modalCtrl: ModalController,
        private actionSheetCtrl: ActionSheetController,
        private metodosUi: Metodosui
    ) { }

    ngOnInit() {
        if (this.initialDetails && Array.isArray(this.initialDetails)) {
            this.details = JSON.parse(JSON.stringify(this.initialDetails));
        }
        this.unit = this.initialUnit || 'm';

        // Fix unit display: ensure it looks like Area (m² or cm²)
        // If it comes as "m2", keep it "m²" (replace 2 with super 2 if needed or just trust string)
        // If "m", make it "m²".
        if (this.unit === 'm' || this.unit === 'cm') {
            this.unit += '²';
        } else if (this.unit === 'm2') {
            this.unit = 'm²'; // Prettier
        }

        this.calculateTotal();
    }

    close() {
        this.modalCtrl.dismiss();
    }

    isValid() {
        return this.current.ancho > 0 && this.current.alto > 0;
    }

    calculateTotal() {
        this.totalArea = this.details.reduce((acc, item) => acc + (Number(item.area) || 0), 0);
    }

    addDetail() {
        if (!this.isValid()) return;

        // Calculate individual area
        // Ensure normalization if units differ? For now assume input unit matches display unit or just display as is?
        // User requested "m or cm".
        // If unit is cm, convert to m for total? Or keep separate?
        // Let's normalize to m for Total if mixed?
        // Simple logic: Area = w * h.

        let w = Number(this.current.ancho);
        let h = Number(this.current.alto);

        // Normalization to m2 if unit is cm
        // If unit is cm, w/100 * h/100
        // But store raw values too.

        let area = w * h;

        // If current unit is cm, and we want total in m2?
        // Usually construction uses m2.
        // Let's assume user wants Total in m2 always.

        let areaM2 = area;

        let conversion = 1;

        if (this.current.unidad === 'cm') {
            conversion = 0.01;
        } else if (this.current.unidad === 'in') {
            conversion = 0.0254;
        } else if (this.current.unidad === 'ft') {
            conversion = 0.3048;
        }

        if (conversion !== 1) {
            areaM2 = (w * conversion) * (h * conversion);
        }

        this.details.push({
            descripcion: this.current.descripcion || `Medida ${this.details.length + 1}`,
            ancho: w,
            alto: h,
            unidad: this.current.unidad,
            area: areaM2,
            foto: this.current.foto,
            originalFoto: this.current.originalFoto,
            annotations: this.current.annotations
        });

        this.calculateTotal();

        // Reset form
        this.current = {
            descripcion: '',
            ancho: 0,
            alto: 0,
            unidad: 'm',
            foto: null,
            originalFoto: null,
            annotations: []
        };
    }

    editDetail(index: number) {
        const item = this.details[index];

        // Restore to current form
        this.current = {
            descripcion: item.descripcion,
            ancho: item.ancho,
            alto: item.alto,
            unidad: item.unidad,
            foto: item.foto,
            originalFoto: item.originalFoto,
            annotations: item.annotations || []
        };

        // Remove from list so it can be updated
        this.details.splice(index, 1);
        this.calculateTotal();
    }

    removeDetail(index: number) {
        this.details.splice(index, 1);
        this.calculateTotal();
    }

    async takePhoto() {
        const image = await this.metodosUi.takePicture();
        if (image?.base64String) {
            // New photo -> set as original
            this.current.originalFoto = image.base64String;
            this.current.annotations = []; // Reset elements
            this.processEditor(this.current.originalFoto);
        }
    }

    async processEditor(base64: string) {
        // Open Image Editor with Original Photo (no text) and Annotations (if any)
        const props = {
            imageBase64: base64,
            initialElements: this.current.annotations
        };

        const editor = await this.metodosUi.openModal(ImageEditorComponent, props);
        if (editor) {
            // Check if it's the new object format or just string
            if (typeof editor === 'string') {
                this.current.foto = editor;
            } else if (editor.base64) {
                this.current.foto = editor.base64;

                // Save elements for re-editing
                if (editor.elements) {
                    this.current.annotations = editor.elements;
                }

                // Auto-fill extracted values if available
                if (editor.extractedValues && editor.extractedValues.length > 0) {
                    const vals = editor.extractedValues;

                    if (vals.length === 1) {
                        this.current.ancho = vals[0];
                    } else if (vals.length >= 2) {
                        this.current.ancho = vals[0];
                        this.current.alto = vals[1];
                    }
                }
            }
        } else {
            // If canceled
            if (!this.current.foto) { // Only if new
                this.current.foto = base64;
            }
        }
    }

    async manageImage() {
        if (!this.current.foto) return;

        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Opciones de Imagen',
            buttons: [
                {
                    text: 'Editar / Ver',
                    icon: 'create',
                    handler: () => {
                        // Use original foto for clean background if available
                        const bg = this.current.originalFoto || this.current.foto;
                        this.processEditor(bg);
                    }
                },
                {
                    text: 'Eliminar Foto',
                    role: 'destructive',
                    icon: 'trash',
                    handler: () => {
                        this.current.foto = null;
                    }
                },
                {
                    text: 'Cancelar',
                    icon: 'close',
                    role: 'cancel'
                }
            ]
        });
        await actionSheet.present();
    }

    finish() {
        this.modalCtrl.dismiss({
            totalArea: this.totalArea,
            details: this.details
        });
    }

}
